import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import fetchLot from '../api/fetchLot';

/**
 * Single-lot query, not cached — a bag's status changes from other
 * pages (inspection, assembly, quarantine) and must be fresh on every
 * visit. Also returns `invalidate` so mutation callers
 * can refetch this lot without knowing the query key.
 */
export default function useLot(lotId) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['lot', lotId],
    queryFn: () => fetchLot(lotId),
    enabled: !!lotId,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['lot', lotId] }),
    [queryClient, lotId],
  );

  return { ...query, invalidate };
}

/**
 * Parameterized invalidator for mutations that change lots other than the
 * one being viewed (e.g. assembly decrementing its scanned source bags) —
 * drops the cached detail query so any open page refetches.
 */
export function useInvalidateLot() {
  const queryClient = useQueryClient();
  return useCallback(
    (lotId) => queryClient.invalidateQueries({ queryKey: ['lot', lotId] }),
    [queryClient],
  );
}
